import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import axiosInstance from 'axiosInstance';
import PageHeader from 'components/common/PageHeader';
import SalesLocationMapCard from './SalesLocationMapCard';
import { getColor, rgbaColor } from 'helpers/utils';
import { getMercadoLibreLabel } from 'helpers/mlLabels';
import LogisticsLabel from 'modules/posts/components/LogisticsLabel';

echarts.use([
  GridComponent,
  LegendComponent,
  TooltipComponent,
  BarChart,
  LineChart,
  CanvasRenderer
]);

const formatDateTime = (value, fallback) => {
  if (!value) return fallback;
  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) return value;
  return parsedValue.toLocaleString();
};

const formatShortDate = value => {
  if (!value) return '';
  const parsedValue = new Date(''.concat(value, 'T12:00:00'));
  if (Number.isNaN(parsedValue.getTime())) return value;
  return parsedValue.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
};

const formatNumber = value => {
  const numericValue = Number(value || 0);
  return numericValue.toLocaleString();
};

const formatAmount = (value, currencyId) => {
  const numericValue = Number(value || 0);
  if (!currencyId) {
    return numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyId,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericValue);
  } catch (error) {
    return ''.concat(currencyId, ' ', numericValue.toFixed(0));
  }
};

const formatPercent = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return `${Math.round(numericValue * 100)}%`;
};

const formatPercentFrom100 = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return `${numericValue.toFixed(1)}%`;
};

const formatDurationShort = totalSeconds => {
  const normalized = Math.max(Number(totalSeconds || 0), 0);
  const minutes = Math.floor(normalized / 60);
  const seconds = Math.floor(normalized % 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${String(remainingMinutes).padStart(2, '0')}m`;
  }
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const buildParetoSummary = analytics => {
  const paretoData = analytics?.pareto;
  const categories = Array.isArray(analytics?.categories) ? analytics.categories : [];

  if (paretoData && Number(paretoData.total_entities || 0) > 0) {
    return {
      thresholdRatio: Number(paretoData.threshold_ratio || 0.8),
      thresholdRevenue: Number(paretoData.threshold_revenue || 0),
      totalEntities: Number(paretoData.total_entities || 0),
      totalRevenue: Number(paretoData.total_revenue || 0),
      vitalCount: Number(paretoData.vital_count || 0),
      vitalRevenue: Number(paretoData.vital_revenue || 0),
      vitalShare: Number(paretoData.vital_share || 0),
      revenueShare: Number(paretoData.revenue_share || 0),
      longTailCount: Number(paretoData.long_tail_count || 0),
      longTailRevenue: Number(paretoData.long_tail_revenue || 0),
      items: Array.isArray(paretoData.items) ? paretoData.items : []
    };
  }

  if (!categories.length) {
    return {
      thresholdRatio: 0.8,
      thresholdRevenue: 0,
      totalEntities: 0,
      totalRevenue: 0,
      vitalCount: 0,
      vitalRevenue: 0,
      vitalShare: 0,
      revenueShare: 0,
      longTailCount: 0,
      longTailRevenue: 0,
      items: []
    };
  }

  const sortedCategories = [...categories]
    .map(item => ({
      key: item.key,
      label: item.label,
      sales: Number(item.sales || 0),
      units: Number(item.units || 0),
      revenue: Number(item.revenue || 0)
    }))
    .sort((left, right) => right.revenue - left.revenue);

  const totalRevenue = sortedCategories.reduce((acc, item) => acc + item.revenue, 0);
  const thresholdRatio = 0.8;
  const thresholdRevenue = totalRevenue * thresholdRatio;
  let cumulativeRevenue = 0;
  let vitalCount = 0;

  const items = sortedCategories.map(item => {
    cumulativeRevenue += item.revenue;
    if (cumulativeRevenue <= thresholdRevenue || vitalCount === 0) {
      vitalCount += 1;
    }
    const revenueShare = totalRevenue > 0 ? item.revenue / totalRevenue : 0;
    const cumulativeShare = totalRevenue > 0 ? cumulativeRevenue / totalRevenue : 0;
    return {
      ...item,
      revenue_share: revenueShare,
      cumulative_share: cumulativeShare
    };
  });

  const vitalRevenue = items.slice(0, vitalCount).reduce((acc, item) => acc + item.revenue, 0);

  return {
    thresholdRatio,
    thresholdRevenue,
    totalEntities: sortedCategories.length,
    totalRevenue,
    vitalCount,
    vitalRevenue,
    vitalShare: sortedCategories.length ? vitalCount / sortedCategories.length : 0,
    revenueShare: totalRevenue > 0 ? vitalRevenue / totalRevenue : 0,
    longTailCount: Math.max(sortedCategories.length - vitalCount, 0),
    longTailRevenue: Math.max(totalRevenue - vitalRevenue, 0),
    items
  };
};

const SELLER_LEVEL_SEGMENTS = [
  { key: '1_red', color: '#f8d7da' },
  { key: '2_orange', color: '#fde2c4' },
  { key: '3_yellow', color: '#ffe066' },
  { key: '4_light_green', color: '#dff3df' },
  { key: '5_green', color: '#d4edda' }
];

const getToneBadgeClassName = tone => {
  if (tone === 'success') return 'badge-soft-success';
  if (tone === 'warning') return 'badge-soft-warning';
  if (tone === 'danger') return 'badge-soft-danger';
  if (tone === 'info') return 'badge-soft-info';
  if (tone === 'primary') return 'badge-soft-primary';
  return 'badge-soft-secondary';
};

const RatingsDistribution = ({ ratings, t }) => {
  const segments = [
    { key: 'positive', label: t('DASHBOARD_HOME.FIELDS.POSITIVE'), value: Number(ratings?.positive || 0), color: '#2eb85c' },
    { key: 'neutral', label: t('DASHBOARD_HOME.FIELDS.NEUTRAL'), value: Number(ratings?.neutral || 0), color: '#f9c74f' },
    { key: 'negative', label: t('DASHBOARD_HOME.FIELDS.NEGATIVE'), value: Number(ratings?.negative || 0), color: '#e55353' }
  ];

  return (
    <div>
      <div
        className="rounded-pill overflow-hidden d-flex"
        style={{ height: 14, backgroundColor: '#edf2f9' }}
      >
        {segments.map(segment => (
          <div
            key={segment.key}
            style={{
              width: `${Math.max(0, Math.min(100, segment.value * 100))}%`,
              backgroundColor: segment.color,
              minWidth: segment.value > 0 ? 6 : 0,
              borderRight: '2px solid #fff'
            }}
          />
        ))}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-3">
        {segments.map(segment => (
          <div key={segment.key} className="d-flex align-items-center gap-2 fs--1">
            <span
              className="rounded-circle flex-shrink-0"
              style={{ width: 12, height: 12, backgroundColor: segment.color }}
            />
            <span className="text-700">
              {segment.label} ({formatPercent(segment.value)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const normalizeHealthPercent = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  if (numericValue <= 1) return Math.round(numericValue * 100);
  return Math.round(numericValue);
};

const getActivityVariant = type => {
  if (type === 'sale') return 'success';
  if (type === 'question') return 'warning';
  if (type === 'claim') return 'danger';
  if (type === 'message') return 'info';
  if (type === 'notification') return 'primary';
  return 'secondary';
};

const getAttributionVariant = source => {
  if (source === 'bot') return 'success';
  if (source === 'human') return 'info';
  return 'secondary';
};

const normalizeSearchIds = ids => {
  const uniqueIds = [];
  (ids || []).forEach(value => {
    const normalized = String(value || '').trim();
    if (normalized && !uniqueIds.includes(normalized)) {
      uniqueIds.push(normalized);
    }
  });
  return uniqueIds;
};

const MAX_SEARCH_LINK_IDS = 100;

const buildModuleSearchLink = (path, ids, params = null) => {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return '';
  const queryParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    const normalizedValue = String(value ?? '').trim();
    if (normalizedValue) {
      queryParams.set(key, normalizedValue);
    }
  });
  if (queryParams.toString()) {
    return `${normalizedPath}?${queryParams.toString()}`;
  }
  const normalizedIds = normalizeSearchIds(ids);
  if (!normalizedIds.length) return normalizedPath;
  if (normalizedIds.length > MAX_SEARCH_LINK_IDS) return normalizedPath;
  return `${normalizedPath}?search=${encodeURIComponent(normalizedIds.join(','))}`;
};

const DEFAULT_FILTERS = {
  period: 'all',
  startDate: '',
  endDate: ''
};

const buildFilterParams = filters => {
  const params = {
    period: filters.period || 'all'
  };

  if (filters.startDate) {
    params.start_date = filters.startDate;
  }
  if (filters.endDate) {
    params.end_date = filters.endDate;
  }

  return params;
};

const getSalesTrendOptions = (trend, t) => ({
  color: [getColor('primary'), getColor('success')],
  tooltip: {
    trigger: 'axis',
    padding: [8, 12],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-200'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    transitionDuration: 0
  },
  legend: {
    data: [t('DASHBOARD_HOME.CHARTS.ORDERS'), t('DASHBOARD_HOME.CHARTS.PAID_REVENUE')],
    textStyle: { color: getColor('gray-700') },
    top: 0
  },
  grid: { left: 40, right: 45, top: 48, bottom: 32 },
  xAxis: {
    type: 'category',
    data: trend.map(item => formatShortDate(item.date)),
    axisLine: { lineStyle: { color: getColor('gray-300') } },
    axisTick: { show: false },
    axisLabel: { color: getColor('gray-600') }
  },
  yAxis: [
    {
      type: 'value',
      splitLine: { lineStyle: { color: getColor('gray-200'), type: 'dashed' } },
      axisLabel: { color: getColor('gray-600') }
    },
    {
      type: 'value',
      splitLine: { show: false },
      axisLabel: { color: getColor('gray-500') }
    }
  ],
  series: [
    {
      name: t('DASHBOARD_HOME.CHARTS.ORDERS'),
      type: 'bar',
      barWidth: 16,
      data: trend.map(item => item.orders || 0),
      itemStyle: {
        borderRadius: [6, 6, 0, 0],
        color: rgbaColor(getColor('primary'), 0.85)
      }
    },
    {
      name: t('DASHBOARD_HOME.CHARTS.PAID_REVENUE'),
      type: 'line',
      yAxisIndex: 1,
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      data: trend.map(item => item.paid_amount || 0),
      lineStyle: { width: 3, color: getColor('success') },
      itemStyle: {
        color: getColor('success'),
        borderColor: getColor('success'),
        borderWidth: 2
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: rgbaColor(getColor('success'), 0.2) },
            { offset: 1, color: rgbaColor(getColor('success'), 0) }
          ]
        }
      }
    }
  ]
});

const getQuestionTrendOptions = (trend, t) => ({
  color: [getColor('success'), getColor('warning'), getColor('danger')],
  tooltip: {
    trigger: 'axis',
    padding: [8, 12],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-200'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    transitionDuration: 0
  },
  legend: {
    data: [
      t('DASHBOARD_HOME.CHARTS.ANSWERED'),
      t('DASHBOARD_HOME.CHARTS.UNANSWERED'),
      t('DASHBOARD_HOME.CHARTS.FLAGGED')
    ],
    textStyle: { color: getColor('gray-700') },
    top: 0
  },
  grid: { left: 40, right: 12, top: 48, bottom: 32 },
  xAxis: {
    type: 'category',
    data: trend.map(item => formatShortDate(item.date)),
    axisLine: { lineStyle: { color: getColor('gray-300') } },
    axisTick: { show: false },
    axisLabel: { color: getColor('gray-600') }
  },
  yAxis: {
    type: 'value',
    splitLine: { lineStyle: { color: getColor('gray-200'), type: 'dashed' } },
    axisLabel: { color: getColor('gray-600') }
  },
  series: [
    {
      name: t('DASHBOARD_HOME.CHARTS.ANSWERED'),
      type: 'bar',
      stack: 'questions',
      barWidth: 16,
      data: trend.map(item => item.answered || 0),
      itemStyle: { borderRadius: [6, 6, 0, 0] }
    },
    {
      name: t('DASHBOARD_HOME.CHARTS.UNANSWERED'),
      type: 'bar',
      stack: 'questions',
      barWidth: 16,
      data: trend.map(item => item.unanswered || 0)
    },
    {
      name: t('DASHBOARD_HOME.CHARTS.FLAGGED'),
      type: 'bar',
      stack: 'questions',
      barWidth: 16,
      data: trend.map(item => item.flagged || 0)
    }
  ]
});

const getMonthlySalesOptions = (monthly, t, currencyId) => ({
  color: [getColor('info'), getColor('primary')],
  tooltip: {
    trigger: 'axis',
    padding: [8, 12],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-200'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: params => {
      const items = Array.isArray(params) ? params : [];
      const axisValue = (items[0] || {}).axisValue || '-';
      const lines = items.map(item => {
        if (item.seriesIndex === 1) {
          return `${item.marker} ${item.seriesName}: ${formatAmount(item.value || 0, currencyId)}`;
        }
        return `${item.marker} ${item.seriesName}: ${formatNumber(item.value || 0)}`;
      });
      return `${axisValue}<br/>${lines.join('<br/>')}`;
    }
  },
  legend: {
    data: [t('DASHBOARD_HOME.CHARTS.MONTHLY_ORDERS'), t('DASHBOARD_HOME.CHARTS.MONTHLY_REVENUE')],
    textStyle: { color: getColor('gray-700') },
    top: 0
  },
  grid: { left: 40, right: 45, top: 48, bottom: 32 },
  xAxis: {
    type: 'category',
    data: monthly.map(item => item.label),
    axisLine: { lineStyle: { color: getColor('gray-300') } },
    axisTick: { show: false },
    axisLabel: { color: getColor('gray-600') }
  },
  yAxis: [
    {
      type: 'value',
      splitLine: { lineStyle: { color: getColor('gray-200'), type: 'dashed' } },
      axisLabel: { color: getColor('gray-600') }
    },
    {
      type: 'value',
      splitLine: { show: false },
      axisLabel: {
        color: getColor('gray-500'),
        formatter: value => formatAmount(value, currencyId)
      }
    }
  ],
  series: [
    {
      name: t('DASHBOARD_HOME.CHARTS.MONTHLY_ORDERS'),
      type: 'bar',
      barWidth: 18,
      data: monthly.map(item => item.orders || 0),
      itemStyle: {
        borderRadius: [6, 6, 0, 0],
        color: rgbaColor(getColor('info'), 0.85)
      }
    },
    {
      name: t('DASHBOARD_HOME.CHARTS.MONTHLY_REVENUE'),
      type: 'line',
      yAxisIndex: 1,
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      data: monthly.map(item => item.revenue || 0),
      lineStyle: { width: 3, color: getColor('primary') },
      itemStyle: {
        color: getColor('primary'),
        borderColor: getColor('primary'),
        borderWidth: 2
      }
    }
  ]
});

const getQuestionsVsSalesOptions = (trend, t) => ({
  color: [getColor('warning'), getColor('primary')],
  tooltip: {
    trigger: 'axis',
    padding: [8, 12],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-200'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    transitionDuration: 0
  },
  legend: {
    data: [t('DASHBOARD_HOME.CHARTS.QUESTIONS_TOTAL'), t('DASHBOARD_HOME.CHARTS.ORDERS')],
    textStyle: { color: getColor('gray-700') },
    top: 0
  },
  grid: { left: 40, right: 12, top: 48, bottom: 32 },
  xAxis: {
    type: 'category',
    data: trend.map(item => formatShortDate(item.date)),
    axisLine: { lineStyle: { color: getColor('gray-300') } },
    axisTick: { show: false },
    axisLabel: { color: getColor('gray-600') }
  },
  yAxis: {
    type: 'value',
    splitLine: { lineStyle: { color: getColor('gray-200'), type: 'dashed' } },
    axisLabel: { color: getColor('gray-600') }
  },
  series: [
    {
      name: t('DASHBOARD_HOME.CHARTS.QUESTIONS_TOTAL'),
      type: 'bar',
      barWidth: 16,
      data: trend.map(item => item.questions_total || 0),
      itemStyle: {
        borderRadius: [6, 6, 0, 0],
        color: rgbaColor(getColor('warning'), 0.85)
      }
    },
    {
      name: t('DASHBOARD_HOME.CHARTS.ORDERS'),
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      data: trend.map(item => item.sales_orders || 0),
      lineStyle: { width: 3, color: getColor('primary') },
      itemStyle: {
        color: getColor('primary'),
        borderColor: getColor('primary'),
        borderWidth: 2
      }
    }
  ]
});

const MetricCard = ({ icon, label, value, help, accent, link, loading = false }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body>
      {loading ? (
        <DashboardBlockSpinner label={label} minHeight={110} />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 42,
                height: 42,
                backgroundColor: rgbaColor(getColor(accent), 0.12),
                color: getColor(accent)
              }}
            >
              <FontAwesomeIcon icon={icon} />
            </div>
            {link ? (
              <Button as={Link} to={link} variant="link" size="sm" className="p-0 text-decoration-none">
                <FontAwesomeIcon icon="arrow-right" />
              </Button>
            ) : null}
          </div>
          <div className="text-600 fs--2 text-uppercase mb-1">{label}</div>
          <h4 className="mb-1">{value}</h4>
          {help ? <div className="text-600 fs--1">{help}</div> : null}
        </>
      )}
    </Card.Body>
  </Card>
);

const DashboardCardHeader = ({ title, subtitle, action }) => (
  <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
    <div>
      <h5 className="mb-1">{title}</h5>
      {subtitle ? <div className="text-600 fs--1">{subtitle}</div> : null}
    </div>
    {action}
  </div>
);

const BASE_DASHBOARD_SECTIONS = [
  'account',
  'messages',
  'notifications',
  'posts',
  'questions',
  'claims',
  'sales'
];

const PRIORITY_DASHBOARD_SECTIONS = ['account', 'messages', 'notifications'];

const getVisibleSectionNames = sectionMeta =>
  BASE_DASHBOARD_SECTIONS.filter(sectionName => Boolean(sectionMeta?.[sectionName]?.visible));

const isSectionLoaded = (sectionData, sectionName) =>
  Object.prototype.hasOwnProperty.call(sectionData || {}, sectionName);

const buildLocalRecordsSummary = ({ postsSection, salesSection, questionsSection, claimsSection, messagesSection, notificationsSection }) => ({
  publications: postsSection?.visible ? Number(postsSection.count || 0) : 0,
  sales: salesSection?.visible ? Number(salesSection.count || 0) : 0,
  questions: questionsSection?.visible ? Number(questionsSection.count || 0) : 0,
  claims: claimsSection?.visible ? Number(claimsSection.count || 0) : 0,
  conversations: messagesSection?.visible ? Number(messagesSection.conversation_count || 0) : 0,
  notifications: notificationsSection?.visible ? Number(notificationsSection.count || 0) : 0
});

const buildDashboardTotalsData = ({ postsSection, salesSection, questionsSection, claimsSection, messagesSection, notificationsSection }) => {
  const localRecords = buildLocalRecordsSummary({
    postsSection,
    salesSection,
    questionsSection,
    claimsSection,
    messagesSection,
    notificationsSection
  });

  return {
    local_records: localRecords,
    paid_revenue: salesSection?.visible ? Number(salesSection.paid_amount || 0) : 0,
    total_revenue: salesSection?.visible ? Number(salesSection.total_amount || 0) : 0,
    bot_revenue: salesSection?.visible ? Number(salesSection.bot_attributed_revenue || 0) : 0,
    bot_conversions: salesSection?.visible ? Number(salesSection.bot_attributed_count || 0) : 0,
    unanswered_questions: questionsSection?.visible ? Number(questionsSection.unanswered_count || 0) : 0,
    open_claims: claimsSection?.visible ? Number(claimsSection.open_count || 0) : 0,
    unread_messages: messagesSection?.visible ? Number(messagesSection.unread_count || 0) : 0,
    unread_notifications: notificationsSection?.visible ? Number(notificationsSection.unread_count || 0) : 0,
    active_publications: postsSection?.visible ? Number(postsSection.active_count || 0) : 0
  };
};

const buildQuestionsVsSalesSectionData = ({ salesSection, questionsSection, sectionMeta }) => {
  const visible = Boolean(sectionMeta?.questions_vs_sales?.visible);
  const salesVisible = Boolean(salesSection?.visible);
  const questionsVisible = Boolean(questionsSection?.visible);
  const salesTrend = Array.isArray(salesSection?.trend) ? salesSection.trend : [];
  const questionsTrend = Array.isArray(questionsSection?.trend) ? questionsSection.trend : [];
  const questionByDate = {};

  questionsTrend.forEach(row => {
    if (row?.date) {
      questionByDate[row.date] = row;
    }
  });

  return {
    visible,
    questions_total: questionsVisible ? Number(questionsSection.count || 0) : 0,
    questions_answered: questionsVisible ? Number(questionsSection.answered_count || 0) : 0,
    questions_unanswered: questionsVisible ? Number(questionsSection.unanswered_count || 0) : 0,
    answer_rate: questionsVisible ? Number(questionsSection.answer_rate || 0) : 0,
    avg_response_seconds: questionsVisible ? questionsSection.response_metrics?.avg_response_seconds ?? null : null,
    conversion_questions_to_sales:
      questionsVisible && salesVisible && Number(questionsSection.count || 0) > 0
        ? Number(salesSection.count || 0) / Number(questionsSection.count || 1)
        : 0,
    sla_rates: questionsVisible ? (questionsSection.response_metrics?.sla_rates || {}) : {},
    trend: salesTrend.map(row => ({
      date: row.date,
      sales_orders: Number(row.orders || 0),
      questions_total: Number(questionByDate[row.date]?.total || 0)
    }))
  };
};

const buildDashboardInsightsData = ({ postsSection, salesSection, questionsSection }) => {
  const insights = [];
  const questionsVisible = Boolean(questionsSection?.visible);
  const postsVisible = Boolean(postsSection?.visible);
  const salesVisible = Boolean(salesSection?.visible);
  const unansweredQuestions = questionsVisible ? Number(questionsSection.unanswered_count || 0) : 0;

  if (unansweredQuestions > 0) {
    insights.push({
      tone: 'danger',
      key: 'unanswered_questions',
      params: { count: unansweredQuestions },
      target_path: '/questions',
      target_ids: questionsSection.unanswered_question_ids || []
    });
  }

  if (postsVisible && Number(postsSection.out_of_stock_with_demand_count || 0) > 0) {
    insights.push({
      tone: 'danger',
      key: 'out_of_stock_with_demand',
      params: { count: Number(postsSection.out_of_stock_with_demand_count || 0) },
      target_path: '/posts',
      target_ids: postsSection.out_of_stock_with_demand_item_ids || []
    });
  }

  if (postsVisible && Number(postsSection.paused_with_sales_count || 0) > 0) {
    insights.push({
      tone: 'warning',
      key: 'paused_with_sales',
      params: { count: Number(postsSection.paused_with_sales_count || 0) },
      target_path: '/posts',
      target_ids: postsSection.paused_with_sales_item_ids || []
    });
  }

  if (postsVisible && Number(postsSection.low_photo_count || 0) > 0) {
    insights.push({
      tone: 'warning',
      key: 'low_photo_count',
      params: { count: Number(postsSection.low_photo_count || 0) },
      target_path: '/posts',
      target_params: { tag: 'low_photo_count', status_filter: 'active' },
      target_ids: postsSection.low_photo_count_item_ids || []
    });
  }

  if (postsVisible && Number(postsSection.catalog_losing_count || 0) > 0) {
    insights.push({
      tone: 'warning',
      key: 'catalog_losing',
      params: { count: Number(postsSection.catalog_losing_count || 0) },
      target_path: '/posts',
      target_params: { tag: 'catalog_competing', status_filter: 'active' },
      target_ids: postsSection.catalog_losing_item_ids || []
    });
  }

  if (postsVisible && Number(postsSection.catalog_listed_count || 0) > 0) {
    insights.push({
      tone: 'warning',
      key: 'catalog_listed',
      params: { count: Number(postsSection.catalog_listed_count || 0) },
      target_path: '/posts',
      target_params: { tag: 'catalog_listed', status_filter: 'active' },
      target_ids: postsSection.catalog_listed_item_ids || []
    });
  }

  if (salesVisible) {
    const cancelledCount = Number(salesSection.cancelled_count || 0);
    const previousCancelled = Number(salesSection.comparison?.previous_cancelled_count || 0);
    if (cancelledCount > previousCancelled && cancelledCount > 0) {
      insights.push({
        tone: 'danger',
        key: 'cancellations_up',
        params: { current: cancelledCount, previous: previousCancelled }
      });
    }
  }

  if (questionsVisible) {
    const avgResponseSeconds = questionsSection.response_metrics?.avg_response_seconds;
    if (avgResponseSeconds != null && Number(avgResponseSeconds) > 300) {
      insights.push({
        tone: 'warning',
        key: 'high_response_time',
        params: { minutes: Math.round((Number(avgResponseSeconds) / 60) * 10) / 10 },
        target_path: '/questions',
        target_ids: questionsSection.slow_response_question_ids || []
      });
    }
  }

  if (salesVisible) {
    const logistics = Array.isArray(salesSection.analytics?.logistics) ? salesSection.analytics.logistics : [];
    const fullBucket = logistics.find(bucket => String(bucket?.key || '').toLowerCase() === 'fulfillment');
    const nonFullBucket = logistics.reduce(
      (accumulator, bucket) => {
        if (String(bucket?.key || '').toLowerCase() === 'fulfillment') {
          return accumulator;
        }
        return {
          sales: accumulator.sales + Number(bucket?.sales || 0),
          revenue: accumulator.revenue + Number(bucket?.revenue || 0)
        };
      },
      { sales: 0, revenue: 0 }
    );

    if (fullBucket && nonFullBucket.sales > 0) {
      const fullAverage = Number(fullBucket.revenue || 0) / Math.max(Number(fullBucket.sales || 0), 1);
      const nonFullAverage = Number(nonFullBucket.revenue || 0) / Math.max(Number(nonFullBucket.sales || 0), 1);
      if (fullAverage > nonFullAverage) {
        insights.push({
          tone: 'success',
          key: 'full_better_conversion',
          params: {}
        });
      }
    }
  }

  return {
    visible: insights.length > 0,
    items: insights.slice(0, 6)
  };
};

const formatNotificationAmount = (amount, currencyId) => {
  if (amount == null || amount === '') return '';
  return formatAmount(amount, currencyId || '');
};

const getNotificationDetailLink = notification => {
  const metadata = notification?.metadata || {};
  if (metadata.order_id) return `/sales/${metadata.order_id}`;
  if (metadata.claim_id) return `/claims?claim=${encodeURIComponent(metadata.claim_id)}`;
  if (metadata.question_id) return `/questions/${metadata.question_id}`;
  if (metadata.item_id) return `/posts/details/${metadata.item_id}`;
  if (notification?.link && notification.link !== '/social/notifications') return notification.link;
  if (metadata.payment_id) return `/sales?search=${encodeURIComponent(metadata.payment_id)}`;
  return notification?.link || '/social/notifications';
};

const buildNotificationActivityItem = (notification, t, language) => {
  const metadata = notification?.metadata || {};
  const topic = String(metadata.topic_key || metadata.topic || notification?.topic || '').toLowerCase();
  const rawMessage = String(notification?.message || '');
  const status = metadata.status || metadata.item_status || (rawMessage.match(/status:\s*([^|]+)/i) || [])[1] || '';
  const rawTitle = String(notification?.title || '');
  const orderId = metadata.order_id || (rawTitle.match(/order #?(\d+)/i) || [])[1];
  const paymentId = metadata.payment_id || (rawTitle.match(/payment #?(\d+)/i) || [])[1];
  const claimId = metadata.claim_id || (rawTitle.match(/claim #?(\d+)/i) || [])[1];
  const itemId = metadata.item_id || (rawTitle.match(/item update (ML[A-Z]?\d+)/i) || [])[1];

  if (orderId || topic.includes('orders')) {
    const parts = [];
    if (status) parts.push(`${t('DASHBOARD_HOME.ACTIVITY_FIELDS.STATUS')}: ${getMercadoLibreLabel('order', status, language)}`);
    const totalAmount = metadata.total_amount != null
      ? metadata.total_amount
      : (rawMessage.match(/total:\s*([^|]+)/i) || [])[1];
    if (totalAmount != null && totalAmount !== '') {
      parts.push(`${t('DASHBOARD_HOME.ACTIVITY_FIELDS.TOTAL')}: ${formatNotificationAmount(totalAmount, metadata.currency_id)}`);
    }
    return {
      title: orderId ? t('DASHBOARD_HOME.ACTIVITY_TITLES.ORDER', { id: orderId }) : t('DASHBOARD_HOME.ACTIVITY_TITLES.ORDER_UPDATE'),
      subtitle: parts.join(' | ') || notification.message || '',
      link: orderId ? `/sales/${orderId}` : getNotificationDetailLink(notification)
    };
  }

  if (paymentId || topic.includes('payments')) {
    return {
      title: paymentId ? t('DASHBOARD_HOME.ACTIVITY_TITLES.PAYMENT', { id: paymentId }) : t('DASHBOARD_HOME.ACTIVITY_TITLES.PAYMENT_UPDATE'),
      subtitle: status ? getMercadoLibreLabel('payment', status, language) : (notification.message || ''),
      link: metadata.order_id ? `/sales/${metadata.order_id}` : getNotificationDetailLink(notification)
    };
  }

  if (claimId || topic.includes('claims')) {
    return {
      title: claimId ? t('DASHBOARD_HOME.ACTIVITY_TITLES.CLAIM', { id: claimId }) : t('DASHBOARD_HOME.ACTIVITY_TITLES.CLAIM_UPDATE'),
      subtitle: [metadata.reason_name, metadata.item_title, status].filter(Boolean).join(' | ') || notification.message || '',
      link: claimId ? `/claims?claim=${encodeURIComponent(claimId)}` : getNotificationDetailLink(notification)
    };
  }

  if (itemId || topic.includes('items')) {
    return {
      title: itemId ? t('DASHBOARD_HOME.ACTIVITY_TITLES.PUBLICATION', { id: itemId }) : t('DASHBOARD_HOME.ACTIVITY_TITLES.PUBLICATION_UPDATE'),
      subtitle: metadata.item_title || (status ? getMercadoLibreLabel('publication', status, language) : '') || notification.message || '',
      link: itemId ? `/posts/details/${itemId}` : getNotificationDetailLink(notification)
    };
  }

  return {
    title: notification.title || t('DASHBOARD_HOME.ACTIVITY_TITLES.NOTIFICATION'),
    subtitle: notification.message || '',
    link: getNotificationDetailLink(notification)
  };
};

const buildActivityFeedData = ({ salesSection, questionsSection, claimsSection, messagesSection, notificationsSection, t, language }) => {
  const feed = [];

  if (salesSection?.visible) {
    (salesSection.recent_sales || []).slice(0, 4).forEach(sale => {
      feed.push({
        type: 'sale',
        title: t('DASHBOARD_HOME.ACTIVITY_TITLES.ORDER', { id: sale.order_id }),
        subtitle: sale.buyer_name || sale.item_summary || '',
        link: sale.order_id ? `/sales/${sale.order_id}` : '/sales',
        occurred_at: sale.date_created
      });
    });
  }

  if (questionsSection?.visible) {
    (questionsSection.recent_questions || []).slice(0, 4).forEach(question => {
      feed.push({
        type: 'question',
        title: question.item_title || question.item_id || 'Question',
        subtitle: question.question_text || '',
        link: question.question_id ? `/questions/${question.question_id}` : '/questions',
        occurred_at: question.date_created
      });
    });
  }

  if (claimsSection?.visible) {
    (claimsSection.recent_claims || []).slice(0, 4).forEach(claim => {
      feed.push({
        type: 'claim',
        title: t('DASHBOARD_HOME.ACTIVITY_TITLES.CLAIM', { id: claim.claim_id }),
        subtitle: claim.reason_name || claim.item_title || '',
        link: claim.claim_id ? `/claims?claim=${encodeURIComponent(claim.claim_id)}` : '/claims',
        occurred_at: claim.last_updated_at || claim.date_created
      });
    });
  }

  if (messagesSection?.visible) {
    (messagesSection.recent_conversations || []).slice(0, 4).forEach(conversation => {
      feed.push({
        type: 'message',
        title: conversation.title || 'Conversation',
        subtitle: conversation.preview || '',
        link: '/messages',
        occurred_at: conversation.last_message_at
      });
    });
  }

  if (notificationsSection?.visible) {
    (notificationsSection.recent_notifications || []).slice(0, 4).forEach(notification => {
      const normalizedNotification = buildNotificationActivityItem(notification, t, language);
      feed.push({
        type: 'notification',
        title: normalizedNotification.title,
        subtitle: normalizedNotification.subtitle,
        link: normalizedNotification.link,
        occurred_at: notification.occurred_at
      });
    });
  }

  return feed
    .sort((left, right) => String(right?.occurred_at || '').localeCompare(String(left?.occurred_at || '')))
    .slice(0, 10);
};

const DashboardBlockSpinner = ({ label, minHeight = 180 }) => (
  <div
    className="d-flex flex-column align-items-center justify-content-center text-center text-600"
    style={{ minHeight }}
  >
    <Spinner animation="border" className="mb-2" />
    <div className="fs--1">{label}</div>
  </div>
);

const DashboardBlockError = ({ message, minHeight = 180 }) => (
  <div
    className="d-flex flex-column align-items-center justify-content-center text-center"
    style={{ minHeight }}
  >
    <Alert variant="warning" className="mb-0 py-2 px-3">
      {message}
    </Alert>
  </div>
);

const Dashboard = ({
  showCoreSections = true,
  showExtendedSections = true,
  hideHeader = false,
  showFilters = true,
  showFeedSections = true,
  hideSalesMap = false,
  showOnlyMap = false,
  externalFilters = null
}) => {
  const { t, i18n } = useTranslation();
  const [bootstrap, setBootstrap] = useState(null);
  const [sectionData, setSectionData] = useState({});
  const [sectionLoading, setSectionLoading] = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [salesMap, setSalesMap] = useState(null);
  const [salesMapLoading, setSalesMapLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const activeRequestIdRef = useRef(0);
  const sectionTimerIdsRef = useRef([]);

  const mlLabel = useCallback((category, value, fallback) => (
    getMercadoLibreLabel(category, value, i18n.language, fallback || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE'))
  ), [i18n.language, t]);

  const clearSectionTimers = useCallback(() => {
    sectionTimerIdsRef.current.forEach(timerId => {
      clearTimeout(timerId);
    });
    sectionTimerIdsRef.current = [];
  }, []);

  const loadDashboardSection = useCallback(async (sectionName, activeFilters, requestId) => {
    setSectionLoading(currentValue => ({
      ...currentValue,
      [sectionName]: true
    }));
    setSectionErrors(currentValue => ({
      ...currentValue,
      [sectionName]: ''
    }));

    try {
      const response = await axiosInstance.get(`modules/dashboard/section/${sectionName}`, {
        params: {
          ...buildFilterParams(activeFilters),
          include_sales_map: 0
        }
      });
      if (requestId !== activeRequestIdRef.current) {
        return;
      }
      setSectionData(currentValue => ({
        ...currentValue,
        [sectionName]: response.data
      }));
    } catch (requestError) {
      if (requestId !== activeRequestIdRef.current) {
        return;
      }
      console.error(`Error loading dashboard section "${sectionName}":`, requestError);
      setSectionErrors(currentValue => ({
        ...currentValue,
        [sectionName]:
          requestError?.response?.data?.error || t('DASHBOARD_HOME.ERRORS.LOAD')
      }));
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setSectionLoading(currentValue => ({
          ...currentValue,
          [sectionName]: false
        }));
      }
    }
  }, [t]);

  const scheduleSectionLoads = useCallback((sectionMeta, activeFilters, requestId) => {
    clearSectionTimers();
    const visibleSections = getVisibleSectionNames(sectionMeta);
    const orderedSections = [
      ...PRIORITY_DASHBOARD_SECTIONS.filter(sectionName => visibleSections.includes(sectionName)),
      ...visibleSections.filter(sectionName => !PRIORITY_DASHBOARD_SECTIONS.includes(sectionName))
    ];

    orderedSections.forEach((sectionName, index) => {
      const delay = index < PRIORITY_DASHBOARD_SECTIONS.length ? index * 60 : 180 + (index - PRIORITY_DASHBOARD_SECTIONS.length) * 90;
      const timerId = setTimeout(() => {
        if (requestId !== activeRequestIdRef.current) {
          return;
        }
        loadDashboardSection(sectionName, activeFilters, requestId);
      }, delay);
      sectionTimerIdsRef.current.push(timerId);
    });
  }, [clearSectionTimers, loadDashboardSection]);

  const loadDashboard = useCallback(async activeFilters => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    clearSectionTimers();
    setLoading(true);
    setSectionData({});
    setSectionErrors({});
    setSectionLoading({});
    setSalesMap(null);
    setSalesMapLoading(false);

    try {
      const response = await axiosInstance.get('modules/dashboard/bootstrap', {
        params: buildFilterParams(activeFilters)
      });
      if (requestId !== activeRequestIdRef.current) {
        return;
      }
      setBootstrap(response.data);
      setError('');
      scheduleSectionLoads(response.data?.sections || {}, activeFilters, requestId);
    } catch (requestError) {
      if (requestId !== activeRequestIdRef.current) {
        return;
      }
      console.error('Error loading dashboard:', requestError);
      setError(
        requestError?.response?.data?.error || t('DASHBOARD_HOME.ERRORS.LOAD')
      );
      setBootstrap(null);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [clearSectionTimers, scheduleSectionLoads, t]);

  const loadSalesMap = useCallback(async activeFilters => {
    if (hideSalesMap || showExtendedSections === false && !showOnlyMap) {
      setSalesMap(null);
      return;
    }

    setSalesMapLoading(true);
    try {
      const response = await axiosInstance.get('modules/dashboard/sales-map', {
        params: buildFilterParams(activeFilters)
      });
      setSalesMap(response.data);
    } catch (requestError) {
      console.error('Error loading dashboard sales map:', requestError);
      setSalesMap(null);
    } finally {
      setSalesMapLoading(false);
    }
  }, [hideSalesMap, showExtendedSections, showOnlyMap]);

  useEffect(() => {
    const activeFilters = externalFilters !== null ? externalFilters : filters;
    loadDashboard(activeFilters);
    return () => {
      clearSectionTimers();
    };
  }, [clearSectionTimers, externalFilters, filters, loadDashboard]);

  useEffect(() => {
    const activeFilters = externalFilters !== null ? externalFilters : filters;
    if (!bootstrap?.sections?.sales?.visible || !isSectionLoaded(sectionData, 'sales') || (hideSalesMap && !showOnlyMap)) {
      setSalesMap(null);
      setSalesMapLoading(false);
      return;
    }
    loadSalesMap(activeFilters);
  }, [bootstrap?.sections?.sales?.visible, externalFilters, filters, hideSalesMap, loadSalesMap, sectionData, showOnlyMap]);

  if (loading) {
    return (
      <Card>
        <Card.Body className="py-6 text-center">
          <Spinner animation="border" className="me-2" />
          {t('DASHBOARD_HOME.LOADING')}
        </Card.Body>
      </Card>
    );
  }

  const sectionsMeta = bootstrap?.sections || {};
  const workspace = bootstrap?.workspace || {};
  const filterInfo = bootstrap?.filters || {};
  const accountSection = sectionData.account || sectionsMeta.account || {};
  const postsSection = sectionData.posts || sectionsMeta.posts || {};
  const salesSection = sectionData.sales || sectionsMeta.sales || {};
  const questionsSection = sectionData.questions || sectionsMeta.questions || {};
  const claimsSection = sectionData.claims || sectionsMeta.claims || {};
  const messagesSection = sectionData.messages || sectionsMeta.messages || {};
  const notificationsSection = sectionData.notifications || sectionsMeta.notifications || {};
  const insightsSection = buildDashboardInsightsData({
    postsSection: sectionData.posts,
    salesSection: sectionData.sales,
    questionsSection: sectionData.questions
  });
  const adsPerformanceSection = postsSection.visible
    ? {
        visible: true,
        ...(postsSection.ads_performance || {})
      }
    : (sectionsMeta.ads_performance || {});
  const questionsVsSalesSection = buildQuestionsVsSalesSectionData({
    salesSection: sectionData.sales,
    questionsSection: sectionData.questions,
    sectionMeta: sectionsMeta
  });
  const activityFeed = buildActivityFeedData({
    salesSection: sectionData.sales,
    questionsSection: sectionData.questions,
    claimsSection: sectionData.claims,
    messagesSection: sectionData.messages,
    notificationsSection: sectionData.notifications,
    t,
    language: i18n.language
  });
  const totals = buildDashboardTotalsData({
    postsSection: sectionData.posts,
    salesSection: sectionData.sales,
    questionsSection: sectionData.questions,
    claimsSection: sectionData.claims,
    messagesSection: sectionData.messages,
    notificationsSection: sectionData.notifications
  });
  const summary = {
    workspace,
    filters: filterInfo,
    sections: {
      account: accountSection,
      posts: postsSection,
      sales: salesSection,
      questions: questionsSection,
      claims: claimsSection,
      messages: messagesSection,
      notifications: notificationsSection,
      insights: insightsSection,
      ads_performance: adsPerformanceSection,
      questions_vs_sales: questionsVsSalesSection,
      activity_feed: sectionsMeta.activity_feed || {}
    },
    totals,
    activity_feed: activityFeed
  };
  const sectionNamesLoading = name => Boolean(sectionLoading[name]);
  const sectionNamesError = name => sectionErrors[name] || '';
  const hasPendingVisibleSection = getVisibleSectionNames(sectionsMeta).some(sectionName => sectionNamesLoading(sectionName));
  const getRequiredSectionNames = sectionNames =>
    sectionNames.filter(sectionName => Boolean(sectionsMeta?.[sectionName]?.visible));
  const renderSectionState = (sectionNames, content, { minHeight = 180, waitForAll = false } = {}) => {
    const requiredSections = getRequiredSectionNames(
      Array.isArray(sectionNames) ? sectionNames : [sectionNames]
    );
    if (!requiredSections.length) {
      return content;
    }

    const loadedCount = requiredSections.filter(sectionName => isSectionLoaded(sectionData, sectionName)).length;
    const pending = requiredSections.some(sectionName => sectionNamesLoading(sectionName));
    const firstError = requiredSections
      .map(sectionName => sectionNamesError(sectionName))
      .find(Boolean);
    const needsBlockingState = waitForAll ? loadedCount < requiredSections.length : loadedCount === 0;

    if (needsBlockingState && pending) {
      return <DashboardBlockSpinner label={t('DASHBOARD_HOME.LOADING')} minHeight={minHeight} />;
    }
    if (needsBlockingState && firstError) {
      return <DashboardBlockError message={firstError} minHeight={minHeight} />;
    }
    return content;
  };

  const account = accountSection.account || {};
  const accountVisibility = accountSection.visibility || {};
  const sellerReputation = account.seller_reputation_summary || {};
  const sellerTransactions = sellerReputation.transactions || {};
  const sellerRatings = sellerTransactions.ratings || {};
  const sellerMetrics = sellerReputation.metrics || {};
  const claimsMetric = sellerMetrics.claims || {};
  const delayedMetric = sellerMetrics.delayed_handling_time || {};
  const cancellationsMetric = sellerMetrics.cancellations || {};
  const sellerStatus = account.seller_status_summary || {};
  const statusListLabels = sellerStatus.list_labels || [];
  const localRecords = totals.local_records || {};
  const primarySalesCurrency = salesSection.primary_currency_id || '';
  const healthPercent = normalizeHealthPercent(postsSection.average_health);
  const salesComparison = salesSection.comparison || {};
  const hasCustomDates = Boolean(filterInfo.start_date && filterInfo.end_date);
  const salesAnalytics = salesSection.analytics || {};
  const salesMapPayload = salesMap || {};
  const paretoSummary = buildParetoSummary(salesAnalytics);
  const hasParetoData = paretoSummary.totalEntities > 0 && paretoSummary.totalRevenue > 0;
  const onTimeRate = Math.max(0, 1 - Number(delayedMetric.rate || 0));
  const delayedRealRate = delayedMetric.excluded?.real_rate;
  const cancellationsRealRate = cancellationsMetric.excluded?.real_rate;
  const hasQuestionsVsSales = Boolean(questionsVsSalesSection.visible);
  const accountSummaryStats = [
    accountVisibility.sales
      ? {
          key: 'sales',
          value: formatNumber(sellerTransactions.total || 0),
          label: t('DASHBOARD_HOME.FIELDS.SALES')
        }
      : null,
    accountVisibility.posts
      ? {
          key: 'products',
          value: formatNumber(postsSection.count || 0),
          label: t('DASHBOARD_HOME.FIELDS.PRODUCTS')
        }
      : null
  ].filter(Boolean);
  const accountPerformanceStats = [
    accountVisibility.sales
      ? {
          key: 'sales',
          value: formatNumber(sellerTransactions.completed || 0),
          label: t('DASHBOARD_HOME.FIELDS.SALES')
        }
      : null,
    accountVisibility.claims
      ? {
          key: 'claims',
          value: formatPercent(claimsMetric.rate),
          label: t('DASHBOARD_HOME.FIELDS.CLAIMS')
        }
      : null,
    accountVisibility.sales
      ? {
          key: 'on-time',
          value: formatPercent(onTimeRate),
          label: t('DASHBOARD_HOME.FIELDS.ON_TIME')
        }
      : null
  ].filter(Boolean);

  const publicationsLinks = {
    active: buildModuleSearchLink('/posts', postsSection.active_item_ids, { status_filter: 'active' }),
    paused: buildModuleSearchLink('/posts', postsSection.paused_item_ids),
    outOfStock: buildModuleSearchLink('/posts', postsSection.out_of_stock_item_ids, { status_filter: 'out_of_stock' }),
    totalStock: buildModuleSearchLink('/posts', postsSection.publication_item_ids),
    soldUnits: buildModuleSearchLink('/posts', postsSection.sold_item_ids),
    freeShipping: buildModuleSearchLink('/posts', postsSection.free_shipping_item_ids),
    outOfStockWithDemand: buildModuleSearchLink('/posts', postsSection.out_of_stock_with_demand_item_ids),
    highQuestions: buildModuleSearchLink('/posts', postsSection.high_questions_item_ids),
    pausedWithSales: buildModuleSearchLink('/posts', postsSection.paused_with_sales_item_ids),
  };

  const renderPublicationsOverviewRow = (label, value, to, className = '') => {
    const content = (
      <>
        <span>{label}</span>
        <strong>{value}</strong>
      </>
    );
    const rowClassName = `d-flex justify-content-between ${className}`.trim();
    if (!to) {
      return <div className={rowClassName}>{content}</div>;
    }
    return (
      <Link to={to} className={`${rowClassName} text-decoration-none`}>
        {content}
      </Link>
    );
  };

  const applyFilters = () => {
    setFilters({
      period: draftFilters.period || 'all',
      startDate: draftFilters.period === 'custom' ? draftFilters.startDate : '',
      endDate: draftFilters.period === 'custom' ? draftFilters.endDate : ''
    });
  };

  const resetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };

  const metricCards = [
    postsSection.visible
      ? {
          key: 'posts',
          icon: 'box-open',
          accent: 'primary',
          label: t('DASHBOARD_HOME.METRICS.ACTIVE_PUBLICATIONS'),
          value: formatNumber(totals.active_publications),
          help: t('DASHBOARD_HOME.METRICS.TOTAL_LOCAL_PUBLICATIONS', {
            count: postsSection.count || 0
          }),
          link: '/posts',
          loading: sectionNamesLoading('posts') && !isSectionLoaded(sectionData, 'posts')
        }
      : null,
    salesSection.visible
      ? {
          key: 'revenue',
          icon: 'dollar-sign',
          accent: 'success',
          label: t('DASHBOARD_HOME.METRICS.PAID_REVENUE'),
          value: formatAmount(totals.paid_revenue, primarySalesCurrency),
          help: t('DASHBOARD_HOME.METRICS.AVG_TICKET', {
            amount: formatAmount(salesSection.average_ticket, primarySalesCurrency)
          }),
          link: '/sales',
          loading: sectionNamesLoading('sales') && !isSectionLoaded(sectionData, 'sales')
        }
      : null,
    salesSection.visible
      ? {
          key: 'bot-revenue',
          icon: 'robot',
          accent: 'info',
          label: t('DASHBOARD_HOME.METRICS.BOT_REVENUE'),
          value: formatAmount(totals.bot_revenue, primarySalesCurrency),
          help: t('DASHBOARD_HOME.METRICS.BOT_CONVERSIONS', {
            count: totals.bot_conversions || 0
          }),
          link: '/sales',
          loading: sectionNamesLoading('sales') && !isSectionLoaded(sectionData, 'sales')
        }
      : null,
    questionsSection.visible
      ? {
          key: 'questions',
          icon: 'question-circle',
          accent: 'warning',
          label: t('DASHBOARD_HOME.METRICS.UNANSWERED_QUESTIONS'),
          value: formatNumber(totals.unanswered_questions),
          help: t('DASHBOARD_HOME.METRICS.ANSWER_RATE', {
            rate: Math.round(Number(questionsSection.answer_rate || 0) * 100)
          }),
          link: '/questions',
          loading: sectionNamesLoading('questions') && !isSectionLoaded(sectionData, 'questions')
        }
      : null,
    claimsSection.visible
      ? {
          key: 'claims',
          icon: 'exclamation-triangle',
          accent: 'danger',
          label: t('DASHBOARD_HOME.METRICS.OPEN_CLAIMS'),
          value: formatNumber(claimsSection.open_count || 0),
          help: t('DASHBOARD_HOME.METRICS.CLAIMS_TOTAL', {
            count: claimsSection.count || 0
          }),
          link: '/claims',
          loading: sectionNamesLoading('claims') && !isSectionLoaded(sectionData, 'claims')
        }
      : null,
    messagesSection.visible
      ? {
          key: 'messages',
          icon: 'comments',
          accent: 'info',
          label: t('DASHBOARD_HOME.METRICS.UNREAD_MESSAGES'),
          value: formatNumber(totals.unread_messages),
          help: t('DASHBOARD_HOME.METRICS.CONVERSATIONS_TOTAL', {
            count: messagesSection.conversation_count || 0
          }),
          link: '/messages',
          loading: sectionNamesLoading('messages') && !isSectionLoaded(sectionData, 'messages')
        }
      : null,
    notificationsSection.visible
      ? {
          key: 'notifications',
          icon: 'bell',
          accent: 'dark',
          label: t('DASHBOARD_HOME.METRICS.UNREAD_NOTIFICATIONS'),
          value: formatNumber(totals.unread_notifications),
          help: t('DASHBOARD_HOME.METRICS.LOCAL_RECORDS', {
            count:
              (localRecords.publications || 0) +
              (localRecords.sales || 0) +
              (localRecords.questions || 0) +
              (localRecords.claims || 0) +
              (localRecords.conversations || 0) +
              (localRecords.notifications || 0)
          }),
          link: '/social/notifications',
          loading: sectionNamesLoading('notifications') && !isSectionLoaded(sectionData, 'notifications')
        }
      : null
  ].filter(Boolean);

  return (
    <>
      {!hideHeader ? (
        <PageHeader
          title={t('DASHBOARD_HOME.TITLE')}
          description={t('DASHBOARD_HOME.DESCRIPTION')}
          className="mb-3"
          data-tour="dashboard-header"
          data-tour-ready={!hasPendingVisibleSection ? 'dashboard' : undefined}
        >
          <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
            <span className="badge badge-soft-primary rounded-pill">
              {workspace.company_name || t('DASHBOARD_HOME.FIELDS.WORKSPACE')}
            </span>
            <span className="badge badge-soft-info rounded-pill">
              {t('DASHBOARD_HOME.FIELDS.MEMBERS', {
                count: workspace.members_count || 0
              })}
            </span>
            <span className="badge badge-soft-secondary rounded-pill">
              {t('DASHBOARD_HOME.FIELDS.PROFILES', {
                count: workspace.profiles_count || 0
              })}
            </span>
            <span className="badge badge-soft-success rounded-pill">
              {hasPendingVisibleSection ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('DASHBOARD_HOME.LOADING')}
                </>
              ) : (
                t('DASHBOARD_HOME.FIELDS.LOCAL_DATA_POINTS', {
                  count:
                    (localRecords.publications || 0) +
                    (localRecords.sales || 0) +
                    (localRecords.questions || 0) +
                    (localRecords.claims || 0) +
                    (localRecords.conversations || 0) +
                    (localRecords.notifications || 0)
                })
              )}
            </span>
            {showFilters && !externalFilters ? (
              <span className="badge badge-soft-secondary rounded-pill">
                {t(`DASHBOARD_HOME.FILTERS.PERIODS.${String(filterInfo.period || 'all').toUpperCase()}`)}
              </span>
            ) : null}
            {showFilters && !externalFilters && hasCustomDates ? (
              <span className="badge badge-soft-dark rounded-pill">
                {filterInfo.start_date} - {filterInfo.end_date}
              </span>
            ) : null}
          </div>
          {showFilters && !externalFilters ? (
            <Row className="g-3 mt-1 align-items-end" data-tour="dashboard-filters">
              <Col md={4} lg={3}>
                <Form.Label className="mb-1">{t('DASHBOARD_HOME.FILTERS.PERIOD')}</Form.Label>
                <Form.Select
                  size="sm"
                  value={draftFilters.period}
                  onChange={({ target }) =>
                    setDraftFilters(currentValue => ({
                      ...currentValue,
                      period: target.value
                    }))
                  }
                >
                  {['all', 'day', 'week', 'month', 'year', 'custom'].map(option => (
                    <option key={option} value={option}>
                      {t(`DASHBOARD_HOME.FILTERS.PERIODS.${option.toUpperCase()}`)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} lg={3}>
                <Form.Label className="mb-1">{t('DASHBOARD_HOME.FILTERS.START_DATE')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={draftFilters.startDate}
                  disabled={draftFilters.period !== 'custom'}
                  onChange={({ target }) =>
                    setDraftFilters(currentValue => ({
                      ...currentValue,
                      startDate: target.value
                    }))
                  }
                />
              </Col>
              <Col md={4} lg={3}>
                <Form.Label className="mb-1">{t('DASHBOARD_HOME.FILTERS.END_DATE')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={draftFilters.endDate}
                  disabled={draftFilters.period !== 'custom'}
                  onChange={({ target }) =>
                    setDraftFilters(currentValue => ({
                      ...currentValue,
                      endDate: target.value
                    }))
                  }
                />
              </Col>
              <Col md={12} lg={3}>
                <div className="d-flex gap-2 flex-wrap">
                  <Button size="sm" onClick={applyFilters}>
                    {t('DASHBOARD_HOME.ACTIONS.APPLY_FILTERS')}
                  </Button>
                  <Button variant="falcon-default" size="sm" onClick={resetFilters}>
                    {t('DASHBOARD_HOME.ACTIONS.CLEAR_FILTERS')}
                  </Button>
                </div>
              </Col>
            </Row>
          ) : null}
        </PageHeader>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {showOnlyMap ? (
        salesSection.visible ? (
          <Row className="g-3 mb-3">
            <Col xs={12}>
              {renderSectionState(
                ['sales'],
                salesMapLoading ? (
                  <Card>
                    <Card.Body className="py-5 text-center">
                      <Spinner animation="border" className="me-2" />
                      {t('DASHBOARD_HOME.LOADING')}
                    </Card.Body>
                  </Card>
                ) : (
                  <SalesLocationMapCard
                    points={salesMapPayload.data?.points || salesMapPayload.points || []}
                    topLocations={salesMapPayload.data?.top_locations || salesMapPayload.top_locations || []}
                    currencyId={primarySalesCurrency}
                    t={t}
                  />
                ),
                { minHeight: 240, waitForAll: true }
              )}
            </Col>
          </Row>
        ) : (
          <div className="text-center text-600 py-4">{t('DASHBOARD_HOME.EMPTY.SALES_MAP')}</div>
        )
      ) : (
      <>

      {showCoreSections && accountSection.visible && isSectionLoaded(sectionData, 'account') && !accountSection.connected ? (
        <Alert variant="warning" className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div className="fw-semi-bold">{t('DASHBOARD_HOME.CONNECT_TITLE')}</div>
            <div className="text-700">{t('DASHBOARD_HOME.CONNECT_DESCRIPTION')}</div>
          </div>
          <Button as={Link} to="/integrations" variant="warning">
            <FontAwesomeIcon icon="plug" className="me-2" />
            {t('DASHBOARD_HOME.CONNECT_ACTION')}
          </Button>
        </Alert>
      ) : null}

      {showCoreSections && insightsSection.visible && insightsSection.items?.length ? (
        <Card className="border-0 shadow-sm mb-3" data-tour="dashboard-insights">
          <Card.Body>
            <DashboardCardHeader
              title={t('DASHBOARD_HOME.SECTIONS.INSIGHTS')}
              subtitle={t('DASHBOARD_HOME.SECTIONS.INSIGHTS_HELP')}
            />
            <div className="d-flex flex-column gap-2">
              {insightsSection.items.map((insight, index) => (
                <Alert
                  key={`${insight.key}-${index}`}
                  variant={
                    insight.tone === 'danger'
                      ? 'danger'
                      : insight.tone === 'warning'
                        ? 'warning'
                        : insight.tone === 'success'
                          ? 'success'
                          : 'info'
                  }
                  className="mb-0 py-2 px-3"
                >
                  {buildModuleSearchLink(insight.target_path, insight.target_ids, insight.target_params) ? (
                    <Link
                      to={buildModuleSearchLink(insight.target_path, insight.target_ids, insight.target_params)}
                      className="text-reset text-decoration-none d-block"
                    >
                      {t(`DASHBOARD_HOME.INSIGHTS.${String(insight.key || '').toUpperCase()}`, insight.params || {})}
                    </Link>
                  ) : (
                    t(`DASHBOARD_HOME.INSIGHTS.${String(insight.key || '').toUpperCase()}`, insight.params || {})
                  )}
                </Alert>
              ))}
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {showCoreSections && metricCards.length ? (
        <Row className="g-3 mb-3" data-tour="dashboard-metrics">
          {metricCards.map(card => (
            <Col md={6} xl={metricCards.length >= 4 ? 3 : 4} key={card.key}>
              <MetricCard {...card} />
            </Col>
          ))}
        </Row>
      ) : null}

      {showCoreSections ? (
      <Row className="g-3 mb-3">
        <Col xl={12}>
          <Card className="h-100 border-0 shadow-sm" data-tour="dashboard-account-health">
            <Card.Body>
              <DashboardCardHeader
                title={t('DASHBOARD_HOME.SECTIONS.ACCOUNT')}
                subtitle={t('DASHBOARD_HOME.SECTIONS.SELLER_REPUTATION_HELP')}
                action={
                  accountSection.visible ? (
                    <Badge bg={accountSection.connected ? 'success' : 'secondary'}>
                      {accountSection.connected
                        ? t('DASHBOARD_HOME.STATUS.CONNECTED')
                        : t('DASHBOARD_HOME.STATUS.NOT_CONNECTED')}
                    </Badge>
                  ) : null
                }
              />
              {renderSectionState(
                ['account'],
                accountSection.connected ? (
                <div
                  className="rounded-3 p-3 p-md-4 border"
                >
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                      <div
                        className="rounded-circle border d-flex align-items-center justify-content-center fw-bold"
                        style={{ width: 64, height: 64, fontSize: 28, backgroundColor: '#fff' }}
                      >
                        {(account.nickname || 'ML').slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="mb-1">{account.nickname || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}</h4>
                        {accountSummaryStats.length ? (
                          <div className="text-700 fs--1 mb-2 d-flex flex-wrap gap-3">
                            {accountSummaryStats.map(stat => (
                              <span key={stat.key}>
                                <strong>{stat.key === 'sales' ? '+' : ''}{stat.value}</strong>{' '}
                                {stat.label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <div className="d-flex flex-wrap gap-2">
                          {account.site_id ? <Badge bg="primary">{account.site_id}</Badge> : null}
                          {account.seller_level_label ? (
                            <span className={`badge rounded-pill ${getToneBadgeClassName(account.seller_level_tone)}`}>
                              {account.seller_level_label}
                            </span>
                          ) : null}
                          {account.seller_power_label ? (
                            <span className={`badge rounded-pill ${getToneBadgeClassName(account.seller_power_tone)}`}>
                              {account.seller_power_label}
                            </span>
                          ) : null}
                          {sellerStatus.site_status_label ? <Badge bg="success">{sellerStatus.site_status_label}</Badge> : null}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column align-items-md-end gap-2">
                      <div className="fs--1 text-700 text-md-end">
                        <div>
                          <span className="text-600">{t('DASHBOARD_HOME.FIELDS.ML_USER_ID')}:</span>{' '}
                          <strong>{account.ml_user_id || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}</strong>
                        </div>
                        <div>
                          <span className="text-600">{t('DASHBOARD_HOME.FIELDS.LOCATION')}:</span>{' '}
                          <strong>
                            {[account.city, account.state, account.country_id].filter(Boolean).join(', ') ||
                              t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}
                          </strong>
                        </div>
                      </div>
                    {account.permalink ? (
                      <Button as="a" href={account.permalink} target="_blank" rel="noreferrer" variant="primary" className="px-4">
                        {t('DASHBOARD_HOME.ACTIONS.OPEN_SELLER')}
                      </Button>
                    ) : null}
                    </div>
                  </div>

                  <div className="d-flex gap-2 mb-3">
                    {SELLER_LEVEL_SEGMENTS.map(segment => (
                      <div
                        key={segment.key}
                        className="flex-1 rounded-pill"
                        style={{
                          height: 12,
                          backgroundColor: segment.color,
                          opacity: sellerReputation.level_id === segment.key ? 1 : 0.45,
                          boxShadow: sellerReputation.level_id === segment.key ? '0 0 0 2px rgba(0,0,0,0.06)' : 'none'
                        }}
                      />
                    ))}
                  </div>

                  {sellerReputation.protection_end_date ? (
                    <Alert variant="info" className="py-2 mb-3">
                      <div className="fw-semi-bold">{t('DASHBOARD_HOME.FIELDS.PROTECTED_SELLER')}</div>
                      <div className="fs--1">
                        {t('DASHBOARD_HOME.FIELDS.PROTECTION_ENDS', {
                          date: formatDateTime(sellerReputation.protection_end_date, sellerReputation.protection_end_date)
                        })}
                      </div>
                    </Alert>
                  ) : null}

                  {accountPerformanceStats.length ? (
                    <Row className="g-3 text-center mb-3">
                      {accountPerformanceStats.map(stat => (
                        <Col xs={12 / Math.min(accountPerformanceStats.length, 3)} key={stat.key}>
                          <div className="fw-bold fs-2">{stat.value}</div>
                          <div className="text-600">{stat.label}</div>
                        </Col>
                      ))}
                    </Row>
                  ) : null}

                  <div className="border-top pt-3">
                    <Row className="g-3 fs--1">
                      {accountVisibility.sales ? (
                        <Col xs={6}>
                          <div className="text-600">{t('DASHBOARD_HOME.FIELDS.SLOW_DELIVERY')}</div>
                          <strong>{formatPercent(delayedMetric.rate)}</strong>
                          {delayedRealRate !== undefined && delayedRealRate !== null ? (
                            <div className="text-600 fs--2">
                              {t('DASHBOARD_HOME.FIELDS.REAL_VALUE')}: {formatPercent(delayedRealRate)}
                            </div>
                          ) : null}
                        </Col>
                      ) : null}
                      {accountVisibility.sales ? (
                        <Col xs={6}>
                          <div className="text-600">{t('DASHBOARD_HOME.FIELDS.CANCELLATIONS')}</div>
                          <strong>{formatPercent(cancellationsMetric.rate)}</strong>
                          {cancellationsRealRate !== undefined && cancellationsRealRate !== null ? (
                            <div className="text-600 fs--2">
                              {t('DASHBOARD_HOME.FIELDS.REAL_VALUE')}: {formatPercent(cancellationsRealRate)}
                            </div>
                          ) : null}
                        </Col>
                      ) : null}
                      {accountVisibility.sales ? (
                        <Col xs={12}>
                          <div className="text-600 mb-2">{t('DASHBOARD_HOME.FIELDS.SELLER_RATING')}</div>
                          <RatingsDistribution ratings={sellerRatings} t={t} />
                        </Col>
                      ) : null}
                      <Col xs={12}>
                        <div className="text-600 mb-1">{t('DASHBOARD_HOME.FIELDS.OPERATIONAL_ALERTS')}</div>
                        <div className="d-flex flex-wrap gap-2">
                          {statusListLabels.length ? statusListLabels.map(label => (
                            <Badge key={label} bg="light" text="dark">{label}</Badge>
                          )) : (
                            <span className="text-700">{t('DASHBOARD_HOME.FIELDS.NO_ACTIVE_ALERTS')}</span>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              ) : (
                <div className="text-600">{t('DASHBOARD_HOME.CONNECT_DESCRIPTION')}</div>
              ),
                { minHeight: 280 }
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      ) : null}

      {showExtendedSections && (adsPerformanceSection.visible || hasQuestionsVsSales) ? (
        <Row className="g-3 mb-3">
          {adsPerformanceSection.visible ? (
            <Col xl={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <DashboardCardHeader
                    title={t('DASHBOARD_HOME.SECTIONS.ADS_PERFORMANCE')}
                    subtitle={t('DASHBOARD_HOME.SECTIONS.ADS_PERFORMANCE_HELP')}
                  />
                  {renderSectionState(
                    ['posts'],
                    <div className="d-flex flex-column gap-2 fs--1">
                      <div className="d-flex justify-content-between">
                        <span>{t('DASHBOARD_HOME.FIELDS.ADS_SPEND')}</span>
                        <strong>{formatAmount(adsPerformanceSection.ads_spend, primarySalesCurrency)}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>{t('DASHBOARD_HOME.FIELDS.ADS_SALES')}</span>
                        <strong>{formatAmount(adsPerformanceSection.ads_sales, primarySalesCurrency)}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>{t('DASHBOARD_HOME.FIELDS.ACOS')}</span>
                        <strong className={adsPerformanceSection.acos_label === 'no_return' ? 'text-danger' : ''}>
                          {adsPerformanceSection.acos_label === 'no_return'
                            ? t('DASHBOARD_HOME.FIELDS.NO_RETURN')
                            : formatPercentFrom100(adsPerformanceSection.acos)}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>{t('DASHBOARD_HOME.FIELDS.TACOS')}</span>
                        <strong>{formatPercentFrom100(adsPerformanceSection.tacos)}</strong>
                      </div>
                    </div>,
                    { minHeight: 160 }
                  )}
                </Card.Body>
              </Card>
            </Col>
          ) : null}

          {hasQuestionsVsSales ? (
            <Col xl={adsPerformanceSection.visible ? 8 : 12}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <DashboardCardHeader
                    title={t('DASHBOARD_HOME.SECTIONS.QUESTIONS_VS_SALES')}
                    subtitle={t('DASHBOARD_HOME.SECTIONS.QUESTIONS_VS_SALES_HELP')}
                  />
                  {renderSectionState(
                    ['sales', 'questions'],
                    <>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <div className="border rounded p-3 h-100">
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.QUESTIONS_TOTAL')}</div>
                        <h5 className="mb-1">{formatNumber(questionsVsSalesSection.questions_total || 0)}</h5>
                        <div className="text-600 fs--2">
                          {t('DASHBOARD_HOME.FIELDS.ANSWERED')}: {formatNumber(questionsVsSalesSection.questions_answered || 0)}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="border rounded p-3 h-100">
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.AVG_RESPONSE_TIME')}</div>
                        <h5 className="mb-1">
                          {questionsVsSalesSection.avg_response_seconds != null
                            ? formatDurationShort(questionsVsSalesSection.avg_response_seconds)
                            : t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}
                        </h5>
                        <div className="text-600 fs--2">
                          {t('DASHBOARD_HOME.FIELDS.SLA_LT_5M')}: {formatPercent(questionsVsSalesSection.sla_rates?.lt_5m || 0)}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="border rounded p-3 h-100">
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.QUESTIONS_TO_SALES_CONVERSION')}</div>
                        <h5 className="mb-1">{formatPercent(questionsVsSalesSection.conversion_questions_to_sales || 0)}</h5>
                        <div className="text-600 fs--2">
                          {t('DASHBOARD_HOME.FIELDS.ANSWER_RATE')}: {formatPercent(questionsVsSalesSection.answer_rate || 0)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <ReactEChartsCore
                    echarts={echarts}
                    option={getQuestionsVsSalesOptions(questionsVsSalesSection.trend || [], t)}
                    style={{ height: 260 }}
                  />
                    </>,
                    { minHeight: 260, waitForAll: true }
                  )}
                </Card.Body>
              </Card>
            </Col>
          ) : null}
        </Row>
      ) : null}

      {showExtendedSections && (salesSection.visible || questionsSection.visible) ? (
        <Row className="g-3 mb-3">
          {salesSection.visible ? (
            <Col xl={7}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <DashboardCardHeader
                    title={t('DASHBOARD_HOME.SECTIONS.SALES_TREND')}
                    subtitle={t('DASHBOARD_HOME.SECTIONS.SALES_TREND_HELP')}
                  />
                  {renderSectionState(
                    ['sales'],
                    <ReactEChartsCore
                      echarts={echarts}
                      option={getSalesTrendOptions(salesSection.trend || [], t)}
                      style={{ height: 320 }}
                    />,
                    { minHeight: 320 }
                  )}
                </Card.Body>
              </Card>
            </Col>
          ) : null}
          {questionsSection.visible ? (
            <Col xl={salesSection.visible ? 5 : 12}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <DashboardCardHeader
                    title={t('DASHBOARD_HOME.SECTIONS.QUESTIONS_TREND')}
                    subtitle={t('DASHBOARD_HOME.SECTIONS.QUESTIONS_TREND_HELP')}
                  />
                  {renderSectionState(
                    ['questions'],
                    <ReactEChartsCore
                      echarts={echarts}
                      option={getQuestionTrendOptions(questionsSection.trend || [], t)}
                      style={{ height: 320 }}
                    />,
                    { minHeight: 320 }
                  )}
                </Card.Body>
              </Card>
            </Col>
          ) : null}
        </Row>
      ) : null}

      {showExtendedSections ? (
      <Row className="g-3 mb-3">
        {postsSection.visible ? (
          <Col xl={3} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader title={t('DASHBOARD_HOME.SECTIONS.PUBLICATIONS_OVERVIEW')} />
                {renderSectionState(
                  ['posts'],
                  <>
                <div className="d-flex flex-column gap-2 fs--1">
                  {renderPublicationsOverviewRow(
                    t('DASHBOARD_HOME.FIELDS.ACTIVE'),
                    formatNumber(postsSection.active_count || 0),
                    publicationsLinks.active
                  )}
                  {renderPublicationsOverviewRow(
                    t('DASHBOARD_HOME.FIELDS.PAUSED'),
                    formatNumber(postsSection.paused_count || 0),
                    publicationsLinks.paused
                  )}
                  {renderPublicationsOverviewRow(
                    t('DASHBOARD_HOME.FIELDS.OUT_OF_STOCK'),
                    formatNumber(postsSection.out_of_stock_count || 0),
                    publicationsLinks.outOfStock
                  )}
                  {renderPublicationsOverviewRow(
                    t('DASHBOARD_HOME.FIELDS.TOTAL_STOCK'),
                    formatNumber(postsSection.total_stock || 0),
                    publicationsLinks.totalStock
                  )}
                  {salesSection.visible ? (
                    renderPublicationsOverviewRow(
                      t('DASHBOARD_HOME.FIELDS.SOLD_UNITS'),
                      formatNumber(postsSection.sold_units || 0),
                      publicationsLinks.soldUnits
                    )
                  ) : null}
                  {renderPublicationsOverviewRow(
                    t('DASHBOARD_HOME.FIELDS.FREE_SHIPPING'),
                    formatNumber(postsSection.free_shipping_count || 0),
                    publicationsLinks.freeShipping
                  )}
                  {renderPublicationsOverviewRow(
                    t('DASHBOARD_HOME.FIELDS.OUT_OF_STOCK_WITH_DEMAND'),
                    formatNumber(postsSection.out_of_stock_with_demand_count || 0),
                    publicationsLinks.outOfStockWithDemand,
                    'text-danger'
                  )}
                  {questionsSection.visible ? (
                    renderPublicationsOverviewRow(
                      t('DASHBOARD_HOME.FIELDS.HIGH_QUESTIONS_PUBLICATIONS'),
                      formatNumber(postsSection.high_questions_count || 0),
                      publicationsLinks.highQuestions,
                      'text-warning'
                    )
                  ) : null}
                  {salesSection.visible ? (
                    renderPublicationsOverviewRow(
                      t('DASHBOARD_HOME.FIELDS.PAUSED_WITH_SALES'),
                      formatNumber(postsSection.paused_with_sales_count || 0),
                      publicationsLinks.pausedWithSales,
                      'text-warning'
                    )
                  ) : null}
                </div>
                {healthPercent !== null ? (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between mb-1 fs--1">
                      <span>{t('DASHBOARD_HOME.FIELDS.AVERAGE_HEALTH')}</span>
                      <strong>{healthPercent}%</strong>
                    </div>
                    <ProgressBar now={healthPercent} variant={healthPercent >= 80 ? 'success' : 'warning'} />
                  </div>
                ) : null}
                  </>,
                  { minHeight: 260 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {salesSection.visible ? (
          <Col xl={3} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader title={t('DASHBOARD_HOME.SECTIONS.SALES_OVERVIEW')} />
                {renderSectionState(
                  ['sales'],
                  <>
                <div className="fs--1 mb-3">
                  <div className="text-600 mb-1">{t('DASHBOARD_HOME.FIELDS.GROSS_TOTAL')}</div>
                  <h4>{formatAmount(salesSection.total_amount, primarySalesCurrency)}</h4>
                </div>
                <div className="d-flex flex-column gap-2 fs--1">
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.PAID')}</span>
                    <strong>{formatNumber(salesSection.paid_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.PENDING')}</span>
                    <strong>{formatNumber(salesSection.pending_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.IN_SHIPPING')}</span>
                    <strong>{formatNumber(salesSection.shipping_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.CANCELLED')}</span>
                    <strong>{formatNumber(salesSection.cancelled_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.PENDING_AMOUNT')}</span>
                    <strong>{formatAmount(salesSection.pending_amount, primarySalesCurrency)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.ATTRIBUTED_SALES')}</span>
                    <strong>{formatNumber(salesSection.attributed_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.BOT_REVENUE')}</span>
                    <strong>{formatAmount(salesSection.bot_attributed_revenue, primarySalesCurrency)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.BOT_CONVERSION')}</span>
                    <strong>{Math.round(Number(salesSection.bot_conversion_rate || 0) * 100)}%</strong>
                  </div>
                </div>
                {salesSection.filters?.is_filtered ? (
                  <div className="mt-3 pt-3 border-top fs--1">
                    <div className="text-600 mb-2">
                      {t('DASHBOARD_HOME.COMPARE.TITLE', {
                        start: salesComparison.compare_start_date || '-',
                        end: salesComparison.compare_end_date || '-'
                      })}
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>{t('DASHBOARD_HOME.COMPARE.PREVIOUS_REVENUE')}</span>
                      <strong>{formatAmount(salesComparison.previous_paid_amount, primarySalesCurrency)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>{t('DASHBOARD_HOME.COMPARE.PREVIOUS_SALES')}</span>
                      <strong>{formatNumber(salesComparison.previous_count || 0)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>{t('DASHBOARD_HOME.COMPARE.PREVIOUS_BOT_REVENUE')}</span>
                      <strong>{formatAmount(salesComparison.previous_bot_revenue, primarySalesCurrency)}</strong>
                    </div>
                  </div>
                ) : null}
                  </>,
                  { minHeight: 260 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {messagesSection.visible ? (
          <Col xl={3} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader title={t('DASHBOARD_HOME.SECTIONS.MESSAGING_OVERVIEW')} />
                {renderSectionState(
                  ['messages'],
                  <div className="d-flex flex-column gap-2 fs--1">
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.UNREAD')}</span>
                    <strong>{formatNumber(messagesSection.unread_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.INTERNAL')}</span>
                    <strong>{formatNumber(messagesSection.internal_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.MERCADO_LIBRE')}</span>
                    <strong>{formatNumber(messagesSection.mercadolibre_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.ML_UNREAD')}</span>
                    <strong>{formatNumber(messagesSection.unread_by_source?.mercadolibre || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.INTERNAL_UNREAD')}</span>
                    <strong>{formatNumber(messagesSection.unread_by_source?.internal || 0)}</strong>
                  </div>
                </div>,
                  { minHeight: 180 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {notificationsSection.visible ? (
          <Col xl={3} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader title={t('DASHBOARD_HOME.SECTIONS.NOTIFICATIONS_OVERVIEW')} />
                {renderSectionState(
                  ['notifications'],
                  <div className="d-flex flex-column gap-2 fs--1">
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.UNREAD')}</span>
                    <strong>{formatNumber(notificationsSection.unread_count || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.SALES')}</span>
                    <strong>{formatNumber(notificationsSection.category_counts?.sales || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.QUESTIONS')}</span>
                    <strong>{formatNumber(notificationsSection.category_counts?.questions || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.MERCADO_LIBRE')}</span>
                    <strong>{formatNumber(notificationsSection.category_counts?.mercadolibre || 0)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{t('DASHBOARD_HOME.FIELDS.INTERNAL')}</span>
                    <strong>{formatNumber(notificationsSection.category_counts?.internal || 0)}</strong>
                  </div>
                </div>,
                  { minHeight: 180 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}
      </Row>
      ) : null}

      {showExtendedSections ? (
      <Row className="g-3 mb-3">
        {postsSection.visible ? (
          <Col xl={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.TOP_PUBLICATIONS')}
                  action={
                    <Button as={Link} to="/posts" variant="falcon-default" size="sm">
                      {t('DASHBOARD_HOME.ACTIONS.VIEW_ALL')}
                    </Button>
                  }
                />
                {renderSectionState(
                  ['posts'],
                  postsSection.top_publications?.length ? (
                  <Table responsive className="mb-0 fs--1">
                    <thead className="bg-light">
                      <tr>
                        <th>{t('DASHBOARD_HOME.TABLE.PUBLICATION')}</th>
                        {salesSection.visible ? (
                          <th>{t('DASHBOARD_HOME.TABLE.SOLD')}</th>
                        ) : null}
                        <th>{t('DASHBOARD_HOME.TABLE.STOCK')}</th>
                        {questionsSection.visible ? (
                          <th>{t('DASHBOARD_HOME.TABLE.QUESTIONS')}</th>
                        ) : null}
                        <th>{t('DASHBOARD_HOME.TABLE.CONVERSION')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.ACOS')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.PERFORMANCE')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postsSection.top_publications.map(item => (
                        <tr key={item.id}>
                          <td>
                            <Link to={`/posts/details/${item.id}`} className="fw-semi-bold">
                              {item.title}
                            </Link>
                            <div className="text-600 fs--2">{mlLabel('publication', item.status)}</div>
                          </td>
                          {salesSection.visible ? (
                            <td>{formatNumber(item.sold_quantity || 0)}</td>
                          ) : null}
                          <td>{formatNumber(item.available_quantity || 0)}</td>
                          {questionsSection.visible ? (
                            <td>
                              {formatNumber(item.total_questions || 0)}
                              {item.unanswered_questions ? (
                                <div className="text-warning fs--2">
                                  {t('DASHBOARD_HOME.FIELDS.UNANSWERED_SHORT', {
                                    count: item.unanswered_questions
                                  })}
                                </div>
                              ) : null}
                            </td>
                          ) : null}
                          <td>{item.conversion_rate != null ? formatPercent(item.conversion_rate) : '-'}</td>
                          <td>
                            {item.ads_metrics?.acos_label === 'no_return'
                              ? t('DASHBOARD_HOME.FIELDS.NO_RETURN')
                              : formatPercentFrom100(item.ads_metrics?.acos)}
                          </td>
                          <td>
                            {item.performance_badge ? (
                              <Badge bg={item.performance_badge.tone === 'danger' ? 'danger' : 'success'}>
                                {t(`DASHBOARD_HOME.BADGES.${String(item.performance_badge.key || '').toUpperCase()}`)}
                              </Badge>
                            ) : (
                              <span className="text-600">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.PUBLICATIONS')}</div>
                ),
                  { minHeight: 220 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        {salesSection.visible ? (
          <Col xl={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.RECENT_SALES')}
                  action={
                    <Button as={Link} to="/sales" variant="falcon-default" size="sm">
                      {t('DASHBOARD_HOME.ACTIONS.VIEW_ALL')}
                    </Button>
                  }
                />
                {renderSectionState(
                  ['sales'],
                  salesSection.recent_sales?.length ? (
                  <Table responsive className="mb-0 fs--1">
                    <thead className="bg-light">
                      <tr>
                        <th>{t('DASHBOARD_HOME.TABLE.ORDER')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.BUYER')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.STATUS')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.TOTAL')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.ORIGIN')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.ATTRIBUTION')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesSection.recent_sales.map(item => (
                        <tr key={item.order_id}>
                          <td>
                            <div className="fw-semi-bold">#{item.order_id}</div>
                            <div className="text-600 fs--2">
                              {formatDateTime(item.date_created, t('DASHBOARD_HOME.SYNC.NEVER'))}
                            </div>
                          </td>
                          <td>{item.buyer_name || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}</td>
                          <td>
                            <Badge bg={item.payment_status === 'approved' || item.payment_status === 'paid' ? 'success' : 'warning'}>
                              {item.payment_status
                                ? mlLabel('payment', item.payment_status)
                                : mlLabel('order', item.status)}
                            </Badge>
                          </td>
                          <td>{formatAmount(item.total_amount, item.currency_id)}</td>
                          <td>
                            <Badge bg={
                              item.origin === 'bot' ? 'success' :
                              item.origin === 'human' ? 'info' :
                              item.origin === 'ads' ? 'warning' :
                              'secondary'
                            }>
                              {t(`DASHBOARD_HOME.ORIGIN.${String(item.origin || 'organic').toUpperCase()}`)}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getAttributionVariant(item.attribution?.source)}>
                              {t(
                                `DASHBOARD_HOME.ATTRIBUTION.${String(item.attribution?.source || 'none').toUpperCase()}`
                              )}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES')}</div>
                ),
                  { minHeight: 220 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}
      </Row>
      ) : null}

      {showExtendedSections && !hideSalesMap && salesSection.visible ? (
        <Row className="g-3 mb-3">
          <Col xs={12}>
            {renderSectionState(
              ['sales'],
              salesMapLoading ? (
                <Card>
                  <Card.Body className="py-5 text-center">
                    <Spinner animation="border" className="me-2" />
                    {t('DASHBOARD_HOME.LOADING')}
                  </Card.Body>
                </Card>
              ) : (
                <SalesLocationMapCard
                  points={salesMapPayload.data?.points || salesMapPayload.points || []}
                  topLocations={salesMapPayload.data?.top_locations || salesMapPayload.top_locations || []}
                  currencyId={primarySalesCurrency}
                  t={t}
                />
              ),
              { minHeight: 260, waitForAll: true }
            )}
          </Col>
        </Row>
      ) : null}

      {showExtendedSections && salesSection.visible ? (
        <Row className="g-3 mb-3">
          <Col xs={12}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.PARETO_80_20')}
                  subtitle={t('DASHBOARD_HOME.SECTIONS.PARETO_80_20_HELP')}
                />

                {renderSectionState(
                  ['sales'],
                  hasParetoData ? (
                  <>
                    <Row className="g-3 mb-3">
                      <Col md={3} sm={6}>
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.VITAL_FEW')}</div>
                        <h5 className="mb-0">
                          {formatNumber(paretoSummary.vitalCount)} / {formatNumber(paretoSummary.totalEntities)}
                        </h5>
                        <div className="text-600 fs--2">
                          {formatPercent(paretoSummary.vitalShare)}
                        </div>
                      </Col>
                      <Col md={3} sm={6}>
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.REVENUE_CAPTURED')}</div>
                        <h5 className="mb-0">{formatAmount(paretoSummary.vitalRevenue, primarySalesCurrency)}</h5>
                        <div className="text-600 fs--2">{formatPercent(paretoSummary.revenueShare)}</div>
                      </Col>
                      <Col md={3} sm={6}>
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.TARGET_80')}</div>
                        <h5 className="mb-0">{formatAmount(paretoSummary.thresholdRevenue, primarySalesCurrency)}</h5>
                        <div className="text-600 fs--2">{Math.round(paretoSummary.thresholdRatio * 100)}%</div>
                      </Col>
                      <Col md={3} sm={6}>
                        <div className="text-600 fs--2 text-uppercase mb-1">{t('DASHBOARD_HOME.FIELDS.LONG_TAIL')}</div>
                        <h5 className="mb-0">{formatNumber(paretoSummary.longTailCount)}</h5>
                        <div className="text-600 fs--2">{formatAmount(paretoSummary.longTailRevenue, primarySalesCurrency)}</div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between fs--1 mb-1">
                        <span>{t('DASHBOARD_HOME.FIELDS.REVENUE_CAPTURED')}</span>
                        <strong>{formatPercent(paretoSummary.revenueShare)}</strong>
                      </div>
                      <ProgressBar
                        now={Math.max(0, Math.min(100, Number(paretoSummary.revenueShare || 0) * 100))}
                        variant={paretoSummary.revenueShare >= paretoSummary.thresholdRatio ? 'success' : 'warning'}
                      />
                    </div>

                    <Table responsive className="mb-0 fs--1">
                      <thead className="bg-light">
                        <tr>
                          <th>{t('DASHBOARD_HOME.TABLE.CATEGORY')}</th>
                          <th>{t('DASHBOARD_HOME.TABLE.SALES_COUNT')}</th>
                          <th>{t('DASHBOARD_HOME.TABLE.TOTAL')}</th>
                          <th>{t('DASHBOARD_HOME.FIELDS.REVENUE_SHARE')}</th>
                          <th>{t('DASHBOARD_HOME.FIELDS.CUMULATIVE_SHARE')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paretoSummary.items.slice(0, 5).map(item => (
                          <tr key={item.key || item.label}>
                            <td>
                              <div className="fw-semi-bold">{item.label || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}</div>
                            </td>
                            <td>{formatNumber(item.sales || 0)}</td>
                            <td>{formatAmount(item.revenue || 0, primarySalesCurrency)}</td>
                            <td>{formatPercent(item.revenue_share || 0)}</td>
                            <td>{formatPercent(item.cumulative_share || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES')}</div>
                ),
                  { minHeight: 280 }
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.MONTHLY_SALES')}
                  subtitle={t('DASHBOARD_HOME.SECTIONS.MONTHLY_SALES_HELP')}
                />
                {renderSectionState(
                  ['sales'],
                  salesAnalytics.monthly?.length ? (
                  <ReactEChartsCore
                    echarts={echarts}
                    option={getMonthlySalesOptions(salesAnalytics.monthly || [], t, primarySalesCurrency)}
                    style={{ height: 300 }}
                  />
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES')}</div>
                ),
                  { minHeight: 300 }
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.SALES_BY_CATEGORY')}
                  subtitle={t('DASHBOARD_HOME.SECTIONS.SALES_BY_CATEGORY_HELP')}
                />
                {renderSectionState(
                  ['sales'],
                  salesAnalytics.categories?.length ? (
                  <Table responsive className="mb-0 fs--1">
                    <thead className="bg-light">
                      <tr>
                        <th>{t('DASHBOARD_HOME.TABLE.CATEGORY')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.SALES_COUNT')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.TOTAL')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesAnalytics.categories.map(item => (
                        <tr key={item.key}>
                          <td>
                            <div className="fw-semi-bold">{item.label || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}</div>
                            <div className="text-600 fs--2">
                              {t('DASHBOARD_HOME.FIELDS.UNITS_SHORT', { count: item.units || 0 })}
                            </div>
                          </td>
                          <td>{formatNumber(item.sales || 0)}</td>
                          <td>{formatAmount(item.revenue, primarySalesCurrency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES')}</div>
                ),
                  { minHeight: 220 }
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.SALES_BY_LOGISTICS')}
                  subtitle={t('DASHBOARD_HOME.SECTIONS.SALES_BY_LOGISTICS_HELP')}
                />
                {renderSectionState(
                  ['sales'],
                  salesAnalytics.logistics?.length ? (
                  <Table responsive className="mb-0 fs--1">
                    <thead className="bg-light">
                      <tr>
                        <th>{t('DASHBOARD_HOME.TABLE.LOGISTICS')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.SALES_COUNT')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.TOTAL')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesAnalytics.logistics.map(item => (
                        <tr key={item.key}>
                          <td>
                            <div className="fw-semi-bold">
                              <LogisticsLabel
                                value={item.key}
                                label={item.label || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}
                              />
                            </div>
                            <div className="text-600 fs--2">
                              {t('DASHBOARD_HOME.FIELDS.UNITS_SHORT', { count: item.units || 0 })}
                            </div>
                          </td>
                          <td>{formatNumber(item.sales || 0)}</td>
                          <td>{formatAmount(item.revenue, primarySalesCurrency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES')}</div>
                ),
                  { minHeight: 220 }
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : null}

      {showFeedSections ? (
      <Row className="g-3">
        {questionsSection.visible ? (
          <Col xl={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <DashboardCardHeader
                  title={t('DASHBOARD_HOME.SECTIONS.RECENT_QUESTIONS')}
                  action={
                    <Button as={Link} to="/questions" variant="falcon-default" size="sm">
                      {t('DASHBOARD_HOME.ACTIONS.VIEW_ALL')}
                    </Button>
                  }
                />
                {renderSectionState(
                  ['questions'],
                  questionsSection.recent_questions?.length ? (
                  <Table responsive className="mb-0 fs--1">
                    <thead className="bg-light">
                      <tr>
                        <th>{t('DASHBOARD_HOME.TABLE.PUBLICATION')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.BUYER')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.STATUS')}</th>
                        <th>{t('DASHBOARD_HOME.TABLE.CREATED')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questionsSection.recent_questions.map(item => (
                        <tr key={item.question_id}>
                          <td>
                            <div className="fw-semi-bold">{item.item_title || item.item_id}</div>
                            <div className="text-600 fs--2">{item.question_text}</div>
                          </td>
                          <td>{item.buyer_label || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')}</td>
                          <td>
                            <Badge
                              bg={
                                item.status_key === 'answered'
                                  ? 'success'
                                  : item.status_key === 'unanswered'
                                  ? 'warning'
                                  : 'danger'
                              }
                            >
                              {t(`DASHBOARD_HOME.STATUS_LABELS.${String(item.status_key || 'unknown').toUpperCase()}`, {
                                defaultValue: item.status_key || t('DASHBOARD_HOME.FIELDS.NOT_AVAILABLE')
                              })}
                            </Badge>
                          </td>
                          <td>{formatDateTime(item.date_created, t('DASHBOARD_HOME.SYNC.NEVER'))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-600">{t('DASHBOARD_HOME.EMPTY.QUESTIONS')}</div>
                ),
                  { minHeight: 220 }
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}

        <Col xl={questionsSection.visible ? 6 : 12}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <DashboardCardHeader title={t('DASHBOARD_HOME.SECTIONS.ACTIVITY_FEED')} />
              {renderSectionState(
                ['sales', 'questions', 'claims', 'messages', 'notifications'],
                summary?.activity_feed?.length ? (
                <div className="d-flex flex-column gap-3">
                  {summary.activity_feed.map((item, index) => (
                    <div key={`${item.type}-${item.occurred_at}-${index}`} className="d-flex gap-3 align-items-start">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center mt-1"
                        style={{
                          width: 34,
                          height: 34,
                          backgroundColor: rgbaColor(getColor(getActivityVariant(item.type)), 0.12),
                          color: getColor(getActivityVariant(item.type))
                        }}
                      >
                        <FontAwesomeIcon
                          icon={
                            item.type === 'sale'
                              ? 'shopping-cart'
                              : item.type === 'question'
                              ? 'question-circle'
                              : item.type === 'claim'
                              ? 'exclamation-triangle'
                              : item.type === 'message'
                              ? 'comments'
                              : 'bell'
                          }
                        />
                      </div>
                      <div className="flex-1">
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="fw-semi-bold">{item.title}</div>
                          <Badge bg={getActivityVariant(item.type)}>
                            {t(`DASHBOARD_HOME.ACTIVITY.${String(item.type || '').toUpperCase()}`)}
                          </Badge>
                        </div>
                        <div className="text-600 fs--1 mb-1">{item.subtitle}</div>
                        <div className="d-flex justify-content-between align-items-center gap-2">
                          <div className="text-500 fs--2">
                            {formatDateTime(item.occurred_at, t('DASHBOARD_HOME.SYNC.NEVER'))}
                          </div>
                          <Button as={Link} to={item.link || '/'} size="sm" variant="link" className="p-0 text-decoration-none">
                            {t('DASHBOARD_HOME.ACTIONS.OPEN')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-600">{t('DASHBOARD_HOME.EMPTY.ACTIVITY')}</div>
              ),
                { minHeight: 220 }
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      ) : null}

      </>
      )}
    </>
  );
};

export default Dashboard;
